import { secToMsec, msecToSec } from 'utils/conversion'
import { SLEEP, ANXIETY, PAIN, AWAKENING } from 'utils/constants'

function playerSessionLogic({
  songInfo,
  indicationType,
  getSelectedDuration,
  player,
  fadePlayer,
  playPauseBtn,
  togglePlayPause,
  volume,
  updateDisplayedPlayTime,
  updateDisplayedDuration,
  eventListenersToRemove,
  onFinishSession,
  isLaunchingAutomatically,
  onCanPlayHandler,
  pauseUnpauseInterval,
}) {
  let mainLoopTime
  let smallLoopTime
  let endLoopTime
  let songDuration
  let requestedDuration
  let fadeToEndLoopDuration
  let fadeInDuration
  let fadeOutDuration
  let startFadeToEndLoopTime
  let startTime
  let endTime
  let ascendingDuration
  let descendingDuration
  let baseDuration
  let fillDuration
  let mainLoopDuration
  let mainLoopCount
  let smallLoopDuration
  let smallLoopCount
  let totalLoopDuration
  let totalPlayDuration
  let mainLoopsDone
  let smallLoopsDone
  let previousTime
  let isPausingUntil = null
  let isUnpausingUntil = null

  function start() {
    player.volume = 0
    // console.log(player)
    player.setAttribute('id', 'current_song')
    mainLoopTime = secToMsec(songInfo.time1)
    smallLoopTime = secToMsec(songInfo.time2)
    endLoopTime = secToMsec(songInfo.time3)
    songDuration = secToMsec(player.duration)

    // 10 seconds fade in/out duration
    fadeInDuration = secToMsec(indicationType === AWAKENING ? 25 : 10)
    fadeOutDuration = secToMsec(indicationType === SLEEP ? 25 : 10)

    // 2 sec fade loop duration
    fadeToEndLoopDuration = secToMsec(2)

    // Start fade 2 seconds before end of end of loop
    startFadeToEndLoopTime = endLoopTime - fadeToEndLoopDuration

    // Get milliseconds from minutes
    requestedDuration = getSelectedDuration() * 1000

    let diffSelectedDuration
    if (songDuration > requestedDuration)
      diffSelectedDuration = 1200000 - requestedDuration
    else diffSelectedDuration = 0

    // Set session dependent parameters
    switch (indicationType) {
      case SLEEP:
        startTime = 0
        endTime = endLoopTime
        ascendingDuration = 0
        descendingDuration = mainLoopTime
        break
      case ANXIETY:
      case PAIN:
        startTime = 0
        endTime = songDuration - diffSelectedDuration
        ascendingDuration = songDuration - diffSelectedDuration - endLoopTime
        descendingDuration = mainLoopTime
        break
      case AWAKENING:
        startTime = mainLoopTime
        endTime = songDuration
        ascendingDuration = songDuration - endLoopTime
        descendingDuration = 0
        break
    }

    // If select J or L session type,  one of them will be 0,
    baseDuration = descendingDuration + ascendingDuration

    // Fill duration is how much we have to 'make up' given the request
    fillDuration = requestedDuration - baseDuration

    mainLoopDuration = endLoopTime - mainLoopTime
    mainLoopCount = Math.floor(fillDuration / mainLoopDuration)

    smallLoopDuration = endLoopTime - smallLoopTime
    // Check how many times small loop fits into what we need to fill after it has been filled with main loop
    smallLoopCount = Math.floor((fillDuration % mainLoopDuration) / smallLoopDuration)

    totalLoopDuration =
      smallLoopDuration * smallLoopCount + mainLoopDuration * mainLoopCount

    totalPlayDuration = baseDuration + totalLoopDuration

    // Set the actual duration in the player display
    const totalPlayDurationSeconds = msecToSec(totalPlayDuration)
    updateDisplayedDuration(totalPlayDurationSeconds)

    // Initialize how many loops are completed
    mainLoopsDone = 0
    smallLoopsDone = 0

    // Variable used for keeping song time at previous player event tick
    previousTime = 0

    setMsecTime(player, startTime)
  }

  function getMsecTime(player) {
    return secToMsec(player.currentTime)
  }

  function setMsecTime(player, ms) {
    player.currentTime = ms / 1000
  }

  function currentTimeToPlayDuration(currentTime) {
    let currentPlayDuration = 0
    // Start time will be non-zero in J session type
    currentTime -= startTime

    // Descending duration will not be 0 in L & U session types
    if (currentTime < descendingDuration) {
      currentPlayDuration = currentTime
      return currentPlayDuration
    }

    currentPlayDuration += descendingDuration
    currentTime -= descendingDuration

    // Loop phase
    if (currentTime < mainLoopDuration) {
      currentPlayDuration += mainLoopDuration * mainLoopsDone
      currentPlayDuration += smallLoopDuration * smallLoopsDone
      if (mainLoopsDone == mainLoopCount)
        currentTime -= mainLoopDuration - smallLoopDuration
      currentPlayDuration += currentTime
      return currentPlayDuration
    }
    currentPlayDuration += totalLoopDuration
    currentTime -= mainLoopDuration

    // Ascending duration will not be 0 in J & U session types
    if (currentTime < ascendingDuration) {
      currentPlayDuration += currentTime
      return currentPlayDuration
    }
    currentPlayDuration += ascendingDuration
    currentTime -= ascendingDuration

    return currentPlayDuration
  }

  function startLoopCrossFade(startTime, deltaTime) {
    if (fadePlayer.paused && !player.paused) {
      setMsecTime(fadePlayer, startTime - deltaTime)
      fadePlayer.play()
    }
  }

  function initiateEndLoopFadeCrossover() {
    player.volume = 0
    if (!isPausingUntil) {
      let unpausingRatio =
        parseFloat(document.getElementById('volume_control').value) / 100

      if (isUnpausingUntil) {
        const current = getMsecTime(player)
        unpausingRatio =
          parseFloat(document.getElementById('volume_control').value) / 100 -
          (isUnpausingUntil - currentTimeToPlayDuration(current)) / pauseUnpauseInterval
      }

      fadePlayer.volume =
        (parseFloat(document.getElementById('volume_control').value) / 100) *
        (unpausingRatio < 0
          ? 0
          : unpausingRatio >
            parseFloat(document.getElementById('volume_control').value) / 100
          ? parseFloat(document.getElementById('volume_control').value) / 100
          : unpausingRatio)
    }
    player.pause()
    switchPlayers()
  }

  fadePlayer.addEventListener('timeupdate', setVolume)

  player.addEventListener('timeupdate', setVolume)

  function resetEventListeners() {
    fadePlayer.removeEventListener('timeupdate', onEndLoopTime)
    fadePlayer.removeEventListener('timeupdate', onEndTime)
    fadePlayer.removeEventListener('timeupdate', updateFadeTime)
    fadePlayer.removeEventListener('timeupdate', updateTime)
    fadePlayer.removeEventListener('ended', onFinish)
    player.addEventListener('timeupdate', onEndLoopTime)
    player.addEventListener('timeupdate', onEndTime)
    player.addEventListener('timeupdate', updateFadeTime)
    player.addEventListener('timeupdate', updateTime)
    player.addEventListener('ended', onFinish)
  }

  function setVolume() {
    player.volume = parseFloat(document.getElementById('volume_control').value) / 100
  }

  function onEndLoopTime() {
    const current = getMsecTime(player)
    const crossedEndLoopTime = previousTime < endLoopTime && current >= endLoopTime

    if (!crossedEndLoopTime) return

    if (mainLoopsDone < mainLoopCount) mainLoopsDone++
    else if (smallLoopsDone < smallLoopCount) smallLoopsDone++

    if (mainLoopsDone < mainLoopCount || smallLoopsDone < smallLoopCount)
      initiateEndLoopFadeCrossover()
  }

  function onEndTime() {
    const crossedEndTime =
      getMsecTime(player) >= endTime && player.currentTime < player.duration

    if (crossedEndTime) {
      player.currentTime = player.duration
      fadePlayer.currentTime = fadePlayer.duration
    }
  }

  function updateTime() {
    const current = getMsecTime(player)
    previousTime = current

    const playTime = currentTimeToPlayDuration(current)
    const playTimeSec = msecToSec(playTime)
    updateDisplayedPlayTime(playTimeSec)
  }

  function fade(deltaTime, fadeDuration) {
    const ratio = deltaTime / fadeDuration
    let pausingRatio = parseFloat(document.getElementById('volume_control').value) / 100
    let unpausingRatio = parseFloat(document.getElementById('volume_control').value) / 100

    if (isPausingUntil) {
      const current = getMsecTime(player)
      pausingRatio =
        (isPausingUntil - currentTimeToPlayDuration(current)) / pauseUnpauseInterval
    }

    if (isUnpausingUntil) {
      const current = getMsecTime(player)
      unpausingRatio =
        parseFloat(document.getElementById('volume_control').value) / 100 -
        (isUnpausingUntil - currentTimeToPlayDuration(current)) / pauseUnpauseInterval
    }

    player.volume =
      ratio *
      (pausingRatio < 0
        ? 0
        : pausingRatio > parseFloat(document.getElementById('volume_control').value) / 100
        ? parseFloat(document.getElementById('volume_control').value) / 100
        : pausingRatio) *
      (unpausingRatio < 0
        ? 0
        : unpausingRatio >
          parseFloat(document.getElementById('volume_control').value) / 100
        ? parseFloat(document.getElementById('volume_control').value) / 100
        : unpausingRatio)

    // fadePlayer will be playing only during crossfade between two loops
    if (!isPausingUntil)
      fadePlayer.volume =
        (parseFloat(document.getElementById('volume_control').value) / 100 - ratio) *
        unpausingRatio
  }

  function updateFadeTime() {
    const currentTime = getMsecTime(player)

    // Humanized conditions used in the 'if' blocks below
    const isDuringStartFadeIn =
      currentTime >= startTime && currentTime < startTime + fadeInDuration
    const isDuringEndFadeOut =
      currentTime >= endTime - fadeOutDuration && currentTime < endTime // endTime is equal to endLoopTime when in session type L
    const isDuringFadeToEndLoopTime =
      currentTime > startFadeToEndLoopTime && currentTime < endLoopTime

    const noLoopsCompleted = mainLoopsDone === 0 && smallLoopsDone === 0
    const isOnLastMainLoop = mainLoopsDone === mainLoopCount - 1 && smallLoopCount === 0
    const isOnLastSmallLoop =
      mainLoopsDone === mainLoopCount && smallLoopsDone === smallLoopCount - 1
    const isOnLastLoop = isOnLastSmallLoop || isOnLastMainLoop

    if (isDuringStartFadeIn && noLoopsCompleted) {
      const deltaTime = currentTime - startTime
      fade(deltaTime, fadeInDuration)
      return
    }

    if (isDuringEndFadeOut && (indicationType !== SLEEP || isOnLastLoop)) {
      const deltaTime = endTime - currentTime
      fade(deltaTime, fadeOutDuration)
      return
    }

    if (!isDuringFadeToEndLoopTime) {
      if (isPausingUntil) {
        const pausingRatio =
          (isPausingUntil - currentTimeToPlayDuration(currentTime)) / pauseUnpauseInterval
        player.volume =
          pausingRatio < 0
            ? 0
            : pausingRatio >
              parseFloat(document.getElementById('volume_control').value) / 100
            ? parseFloat(document.getElementById('volume_control').value) / 100
            : pausingRatio

        return
      }

      if (isUnpausingUntil) {
        const unpausingRatio =
          parseFloat(document.getElementById('volume_control').value) / 100 -
          (isUnpausingUntil - currentTimeToPlayDuration(currentTime)) /
            pauseUnpauseInterval
        player.volume =
          unpausingRatio < 0
            ? 0
            : unpausingRatio >
              parseFloat(document.getElementById('volume_control').value) / 100
            ? parseFloat(document.getElementById('volume_control').value) / 100
            : unpausingRatio

        return
      }

      if (
        player.volume !==
        parseFloat(document.getElementById('volume_control').value) / 100
      )
        player.volume = parseFloat(document.getElementById('volume_control').value) / 100
      return
    }

    // This block may run in any session type (L,U,J)
    const deltaTime = endLoopTime - currentTime

    // If need to loop again, initiate a
    // crossfade between player and fadePlayer
    if (mainLoopsDone < mainLoopCount - 1) {
      fade(deltaTime, fadeToEndLoopDuration)
      startLoopCrossFade(mainLoopTime, deltaTime)
      return
    }

    if (mainLoopsDone === mainLoopCount - 1) {
      // If on last main loop and there are small loops to complete
      if (smallLoopCount) {
        fade(deltaTime, fadeToEndLoopDuration)
        startLoopCrossFade(smallLoopTime, deltaTime)
        return
      }
    }

    if (mainLoopsDone === mainLoopCount) {
      // If main loops completed and there are small loops to complete
      if (smallLoopsDone < smallLoopCount - 1) {
        fade(deltaTime, fadeToEndLoopDuration)
        startLoopCrossFade(smallLoopTime, deltaTime)
        return
      }
    }

    if (isPausingUntil) {
      const pausingRatio =
        (isPausingUntil - currentTimeToPlayDuration(currentTime)) / pauseUnpauseInterval
      player.volume =
        pausingRatio < 0
          ? 0
          : pausingRatio >
            parseFloat(document.getElementById('volume_control').value) / 100
          ? parseFloat(document.getElementById('volume_control').value) / 100
          : pausingRatio
      return
    }

    if (isUnpausingUntil) {
      const unpausingRatio =
        parseFloat(document.getElementById('volume_control').value) / 100 -
        (isUnpausingUntil - currentTimeToPlayDuration(currentTime)) / pauseUnpauseInterval
      player.volume =
        unpausingRatio < 0
          ? 0
          : unpausingRatio >
            parseFloat(document.getElementById('volume_control').value) / 100
          ? parseFloat(document.getElementById('volume_control').value) / 100
          : unpausingRatio
      return
    }

    if (
      player.volume !==
      parseFloat(document.getElementById('volume_control').value) / 100
    )
      player.volume = parseFloat(document.getElementById('volume_control').value) / 100
  }

  function onFinish() {
    // Callback to react Player component page to initiate transition to pain rating page
    onFinishSession()
  }

  function switchPlayers() {
    ;[player, fadePlayer] = [fadePlayer, player]

    resetEventListeners()
  }

  function onClickTogglePlayers() {
    if (isPausingUntil || isUnpausingUntil) return

    const current = getMsecTime(player)
    const playTime = currentTimeToPlayDuration(current)

    if (!player.paused) {
      // Do not pause if near end
      if (playTime + pauseUnpauseInterval >= totalPlayDuration) return

      isPausingUntil = playTime + pauseUnpauseInterval

      setTimeout(() => (isPausingUntil = null), pauseUnpauseInterval)

      togglePlayPause(player, fadePlayer)
      return
    }

    if (player.paused) {
      isUnpausingUntil =
        playTime + pauseUnpauseInterval > totalPlayDuration
          ? totalPlayDuration
          : playTime + pauseUnpauseInterval

      setTimeout(
        () => (isUnpausingUntil = null),
        isUnpausingUntil === totalPlayDuration
          ? totalPlayDuration - playTime
          : pauseUnpauseInterval
      )

      player.volume = 0
      togglePlayPause(player, fadePlayer)
    }
  }

  function onStart() {
    resetEventListeners()
    start()
    playPauseBtn.removeEventListener('click', onStart)
    playPauseBtn.addEventListener('click', onClickTogglePlayers)

    eventListenersToRemove.push({
      target: playPauseBtn,
      event: 'click',
      fn: onClickTogglePlayers,
    })

    togglePlayPause(player, fadePlayer)
  }

  function onCanPlay() {
    // Remvoe listener to prevent successive triggers
    player.removeEventListener('canplaythrough', onCanPlay)

    playPauseBtn.addEventListener('click', onStart)

    // Let player know we can begin play
    onCanPlayHandler()

    // If launching via alarm, start playing automatically
    if (isLaunchingAutomatically) playPauseBtn.click()
  }

  player.addEventListener('canplaythrough', onCanPlay)

  eventListenersToRemove.push(
    {
      target: player,
      event: 'timeupdate',
      fn: [onEndLoopTime, onEndTime, updateFadeTime, updateTime],
    },
    { target: player, event: 'ended', fn: onFinish },
    {
      target: fadePlayer,
      event: 'timeupdate',
      fn: [onEndLoopTime, onEndTime, updateFadeTime, updateTime],
    },
    { target: fadePlayer, event: 'ended', fn: onFinish }
  )
}

export { playerSessionLogic }
